import { AEAD_PROFILE_V1, exportAesGcmKey, generateAesGcmKey, importAesGcmKey } from './aead.js'
import { CryptoError } from './errors.js'
import { toBufferSource } from './encoding.js'

/**
 * Key management — PRD §7.4, §11.2, §14.1.
 * DEKs are AES-256-GCM keys; KEKs wrap them with AES-KW. The exact cipher/KDF
 * and key-access matrix remains the §14.7 security gate: interfaces freeze now,
 * the profile is versioned so a later review can evolve it without breaking data.
 */

export const KEK_PROFILE_V1 = {
  id: 'signbento-kek-v1',
  algorithm: 'AES-KW',
  key_bits: 256,
} as const

const KEK_BYTES = KEK_PROFILE_V1.key_bits / 8

/**
 * Platform-held wrapping key. Web: non-extractable `CryptoKey` in IndexedDB
 * (implemented with the vault in S0.5); native later: Keychain/Keystore.
 */
export interface PlatformKeyStore {
  getOrCreateKek(key_id: string): Promise<CryptoKey>
  deleteKek(key_id: string): Promise<void>
}

export interface KeyWrapper {
  wrap(key: CryptoKey, kek: CryptoKey): Promise<Uint8Array>
  unwrapDek(wrapped: Uint8Array, kek: CryptoKey): Promise<CryptoKey>
}

export function generateDek(): Promise<CryptoKey> {
  return generateAesGcmKey()
}

export async function generateKek(extractable = false): Promise<CryptoKey> {
  return globalThis.crypto.subtle.generateKey(
    { name: 'AES-KW', length: KEK_PROFILE_V1.key_bits },
    extractable,
    ['wrapKey', 'unwrapKey'],
  )
}

export async function importKek(bytes: Uint8Array, extractable = false): Promise<CryptoKey> {
  if (bytes.byteLength !== KEK_BYTES) {
    throw new CryptoError('CRYPTO_INVALID_KEY', `AES-KW-256 requires ${KEK_BYTES} key bytes`)
  }
  return globalThis.crypto.subtle.importKey(
    'raw',
    toBufferSource(bytes),
    { name: 'AES-KW' },
    extractable,
    ['wrapKey', 'unwrapKey'],
  )
}

function assertKek(kek: CryptoKey): void {
  if (kek.algorithm.name !== 'AES-KW' || kek.type !== 'secret') {
    throw new CryptoError('CRYPTO_INVALID_KEY', 'expected an AES-KW secret key')
  }
}

export class AesKwKeyWrapper implements KeyWrapper {
  async wrap(key: CryptoKey, kek: CryptoKey): Promise<Uint8Array> {
    assertKek(kek)
    if (!key.extractable) {
      throw new CryptoError('CRYPTO_INVALID_KEY', 'wrapped key must be extractable')
    }
    try {
      return new Uint8Array(await globalThis.crypto.subtle.wrapKey('raw', key, kek, 'AES-KW'))
    } catch {
      throw new CryptoError('CRYPTO_INVALID_KEY', 'AES-KW wrap failed')
    }
  }

  async unwrapDek(wrapped: Uint8Array, kek: CryptoKey): Promise<CryptoKey> {
    assertKek(kek)
    try {
      return await globalThis.crypto.subtle.unwrapKey(
        'raw',
        toBufferSource(wrapped),
        kek,
        'AES-KW',
        { name: 'AES-GCM', length: AEAD_PROFILE_V1.key_bits },
        true,
        ['encrypt', 'decrypt'],
      )
    } catch {
      throw new CryptoError('CRYPTO_INVALID_KEY', 'AES-KW unwrap failed')
    }
  }
}

/** Export a DEK's raw bytes (extractable only) — used by the backup key schedule. */
export function exportDekRaw(dek: CryptoKey): Promise<Uint8Array> {
  return exportAesGcmKey(dek)
}

/** Import raw DEK bytes (e.g. recovered from a wrapped backup key). */
export function importDekRaw(bytes: Uint8Array, extractable = true): Promise<CryptoKey> {
  return importAesGcmKey(bytes, extractable)
}
